import type { SignalOriginType } from '@terra/shared-schemas-orchestrator';
import {
  formatCategoryForDisplay,
  formatWordWithKnownAcronyms,
} from '@terra/shared-utils';
import {
  getSourceFilterValuesForOrigins,
  SIGNAL_ORIGIN_FILTER_VALUES,
  type SignalOriginFilterValue,
  type SignalSourceFilterValue,
} from './signal-source.util';

const isUnresolvedTranslation = ({
  translated,
  key,
  value,
}: {
  translated: string;
  key: string;
  value: string;
}): boolean =>
  translated === key ||
  translated === value ||
  translated.endsWith(`.${key}`) ||
  translated.endsWith(`.${value}`) ||
  (!translated.includes(' ') &&
    translated.includes(value) &&
    translated.length > value.length);

function formatEnumForDisplay(value: string): string {
  if (value.includes('_')) {
    return formatCategoryForDisplay(value);
  }

  const withSpaces = value.replace(/([a-z])([A-Z])/g, '$1 $2');

  return withSpaces
    .split(' ')
    .filter((word) => word.length > 0)
    .map(formatWordWithKnownAcronyms)
    .join(' ');
}

export function getOriginFilterOptions(
  t: (key: string) => string,
  {
    origins,
  }: {
    origins?: readonly SignalOriginFilterValue[];
  } = {},
): Array<{ label: string; value: SignalOriginFilterValue }> {
  return (origins ?? SIGNAL_ORIGIN_FILTER_VALUES).map((value) => {
    const key = `origin.${value}`;
    const translated = t(key);

    return {
      label: isUnresolvedTranslation({ translated, key, value })
        ? formatEnumForDisplay(value)
        : translated,
      value,
    };
  });
}

export function getSourceFilterOptions(
  t: (key: string) => string,
  {
    origins,
  }: {
    origins?: readonly SignalOriginType[];
  } = {},
): Array<{ label: string; value: SignalSourceFilterValue }> {
  return getSourceFilterValuesForOrigins({ origins }).map((value) => {
    const key = `source.${value}`;
    const translated = t(key);

    return {
      label: isUnresolvedTranslation({ translated, key, value })
        ? formatEnumForDisplay(value)
        : translated,
      value,
    };
  });
}

export function shouldShowSignalSourceFilter({
  hasThirdPartySignals,
  selectedSources,
}: {
  hasThirdPartySignals: boolean | undefined;
  selectedSources?: readonly string[];
}): boolean {
  return hasThirdPartySignals === true || Boolean(selectedSources?.length);
}
